import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { markNotificationRead } from "../../redux/slice/notificationSlice";
import { useToast } from "../../helpers/contexts/ToastContext";
import { handleApiError } from "../../utility/handleApiError";
import { useRolePermission } from "../../hooks/useRolePermission";

// Which part of the CRM a notification is about
export const moduleKey = (module = "") => {
  const m = String(module).toLowerCase();
  if (m.includes("quot")) return "quotation";
  if (m.includes("follow")) return "followup";
  if (m.includes("lead")) return "lead";
  if (m.includes("customer")) return "customer";
  if (m.includes("product")) return "product";
  if (m.includes("user") || m.includes("role")) return "user";
  return "other";
};

// Bootstrap colour + plain label per module (bg-{color}-subtle / text-{color})
export const MODULE_META = {
  lead: { color: "primary", label: "Lead", list: "/lead", path: (id) => `/lead/${id}`, perm: "leads" },
  quotation: {
    color: "success",
    label: "Quotation",
    list: "/quotation",
    path: (id) => `/quotation/${id}`,
    perm: "quotations",
  },
  followup: {
    color: "warning",
    label: "Follow-up",
    list: "/follow-ups",
    path: (id) => `/follow-ups/${id}`,
    perm: "followups",
  },
  customer: {
    color: "info",
    label: "Customer",
    list: "/customer",
    path: (id) => `/customer/${id}`,
    perm: "customers",
  },
  product: { color: "secondary", label: "Product", list: "/products", path: null, perm: "products" },
  user: { color: "secondary", label: "User", list: null, path: null, perm: "userprofile" },
  other: { color: "secondary", label: "Update", list: null, path: null, perm: null },
};

const parseData = (data) => {
  if (typeof data !== "string") return data || {};
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

// The record this notification points to
const relatedIdOf = (item, key) => {
  const d = parseData(item.data);
  const byModule = {
    lead: item.lead_id || d.lead_id,
    quotation: item.quotation_id || d.quotation_id,
    followup: item.followup_id || item.follow_up_id || d.followup_id || d.follow_up_id,
    customer: item.customer_id || d.customer_id,
  };
  return byModule[key] || item.related_id || d.related_id || d.id || null;
};

/**
 * Click on a notification: mark it read, check access, open the record.
 * Same rules for the side panel and the notifications page.
 */
export const useOpenNotification = ({ onRead, onLeave } = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useRolePermission();
  const { user, roleName } = useSelector((state) => state.auth);

  return useCallback(
    async (item) => {
      const isAdmin = ["admin", "super-admin"].includes(String(roleName).toLowerCase());
      const ownerId = item.assigned_to?.id || item.assigned_to;
      if (!isAdmin && user && ownerId && String(ownerId) !== String(user.id)) {
        showToast("You are not authorized to view this record.", "warning");
        return;
      }

      try {
        if (!item.read_at) {
          await dispatch(markNotificationRead(item.id)).unwrap();
          onRead?.();
        }
      } catch (err) {
        handleApiError(err, { showToast });
      }

      const key = moduleKey(item.module);
      const meta = MODULE_META[key];
      if (meta.perm && !hasPermission(meta.perm, "view")) {
        showToast(`You do not have permission to view this ${meta.label.toLowerCase()}.`, "danger");
        return;
      }

      const id = relatedIdOf(item, key);
      if (id && meta.path) {
        onLeave?.();
        navigate(meta.path(id));
      } else if (meta.list) {
        showToast("This update has no record linked, opening the list instead.", "info");
        onLeave?.();
        navigate(meta.list);
      }
    },
    [dispatch, navigate, showToast, hasPermission, user, roleName, onRead, onLeave]
  );
};
